
import Vue from 'vue';
import { defineComponent } from 'vue';
import { mergeBgClass, findClassBorderColor } from '@/scss/StyleClassManager';
import PrzPicture from '@/components/Muffin/Picture.vue';
import VueObserveVisibility from 'vue-observe-visibility';

import { BlockItem } from '@/models/Blocks/Itens/block-item';
import { Picture } from '@/models/Muffin/picture';
import { BlockItemMedia } from '@/models/Blocks/Itens/block-item-media';
import { BlockItemType } from '@/models/Blocks/Itens/Types/block-item-type';
import { TemplateItemType } from '@/models/Blocks/Itens/Types/template-item-type';
import { BlockItemVideo } from '@/models/Blocks/Itens/Types/block-item-video';
import { DeviceType } from "@/models/Blocks/Itens/Types/device-type";
import { BlockEvents } from '@/models/Blocks/block-events';
import appProzis from '@/Utilities/app-prozis-store';

Vue.use(VueObserveVisibility);

export default defineComponent({
    name: 'AbstractItem',
    components: {
        PrzPicture,
        PrzBgVideo: () => import('../Parts/BackgroundVideo/Component.vue')
    },
    props: {
        item: {
            type: BlockItem,
            default: () => new BlockItem(BlockItemType.empty)
        },
        className: {
            type: [Object, String],
            default: null
        },
        styleName: {
            type: String,
            default: null
        },
        ariaLabel: {
            type: String,
            default: null
        },
        anchor: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            contentClass: {},
            inViewport: false,
            alreadyInViewport: false,
            contentInViewport: false,
            videoReady: false,
            borderClass: '',
            borderStyle: ''
        }
    },
    mounted() {
        if (this.video && this.video.poster) {
            this.contentClass = this.className;
        } else {
            let mergedClass = this.item.backgroundColor ? mergeBgClass(this.className, this.item.backgroundColor) : null;
            if (mergedClass) {
                this.contentClass = mergedClass;
            } else {
                this.contentClass = this.className;
            }
        }
        let borderClass = this.item?.hasBorder && this.item?.borderColor ? findClassBorderColor(this.item.borderColor) : null;
        if(borderClass){
            this.borderClass = borderClass;
        }else if(!borderClass && this.item?.hasBorder && this.item?.borderColor){
            this.borderStyle = `border: .5rem solid ${this.item?.borderColor}; border-radius: calc(1.75rem) !important;`;
        }
    },
    computed: {
        marginObserver() {
            return (window.innerHeight || document.documentElement.clientHeight)
        },
        itemClass() {
            let tplType = this.item.templateType;
            let classNames = {
                'prz-mob-grd-item-h': tplType == TemplateItemType.horizontal,
                'prz-mob-grd-item-v': tplType == TemplateItemType.vertical,
                'prz-mob-grd-item-s': tplType == TemplateItemType.square,
                'prz-grd-item-grow': this.item.deviceType == DeviceType.mobile && this.item.type == BlockItemType.promo && tplType == TemplateItemType.horizontal
            };

            let result = this.borderClass ? {...classNames, [this.borderClass]: true} : classNames;

            //Class defined in object
            let cssClass = this.item?.cssClass??'';
            if(cssClass){
                let list = cssClass.split(' ');
                list.forEach( (c) => {
                    result = {...result, [c] : true};
                });
            }
            
            return result;
        },
        images(): Picture | null {
            return this.item instanceof BlockItemMedia ? this.item.images : null;
        },
        video(): BlockItemVideo | null {
            return this.item instanceof BlockItemMedia ? this.item.video : null;
        },
        contentStyle() {
            let hasImages   = this.images && this.images.main ? true : false;
            if(hasImages){
                return this.styleName;
            }
            let mergedClass = this.item.backgroundColor ? mergeBgClass(this.className, this.item.backgroundColor) : null;
            if (mergedClass) {
                return this.styleName;
            } else {
                return this.item.backgroundColor ? (this.styleName ?? '') + ' background-color: ' + this.item.backgroundColor : '';
            }
        }
    },
    methods: {
        onvideoplay(player: any){
            this.videoReady = true;
        },
        goto() {
            const item = this.item;
            const extraInfo = item.extraInfo;
            if(extraInfo && extraInfo.sendEventApp){
                appProzis.call(extraInfo.eventName, JSON.stringify({
                    type: extraInfo.typeSocial,
                    id: extraInfo.id.toString(),
                    url: extraInfo.url,
                    openMode: extraInfo.openMode,
                    eventData: this.getAppEventData(),
                }));
            }else{
                this.$emit('click');
                
                if (!item.url || (item.url=='#')) {
                    BlockEvents.AddActionItemClick(this.$el, {item: item});
                    return false;
                }

                if(item.chatClickId){
                    const el = document.getElementById('sherpaChatContent');
                    if(el){
                        (el as HTMLElement).dispatchEvent(new CustomEvent('chatHelpBlockClick', {detail: {
                                blockId: item.chatClickId,
                            }}));
                    }
                }
    
                //Save position on page
                item && item.savePosition();
                if (item.url) {
                    window.location.assign(item.url);
                }

                return false;
            }
        },
        visibilityChanged(isVisible: boolean) {
            this.inViewport = isVisible;
        },
        visibilityContentChanged(isVisible: boolean) {
            this.contentInViewport = isVisible;
            if(isVisible){
                this.alreadyInViewport = true;
            }

            //Sometimes when it reappears on viewport, the video doesn't play!
            //When this happens, i remove the image blur 
            if(isVisible){
                setTimeout(() => {
                    let video = this.$el.querySelector('video');
                    if(!video && this.videoReady){
                        this.videoReady = false;
                    }
                }, 1000);
            }
        },
        getAppEventData(){
            const mub = window.mub;
            if(!mub){
                return null;
            }
            const d = JSON.parse(this.item.mubData);
            return {
                rid: mub.rid,
                page: mub.page,
                block: d.b ? d.b : '',
                blockType: d.t ? d.t : '',
                blockMeta: d.m ? d.m : null,
            }
        }
    },
});
